import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarClock, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSession } from "@/context/SessionContext";
import { useFetch } from "@/lib/useFetch";
import { fmtDate } from "@/lib/format";
import { PageHeader, ErrorState, LoadingRows, NoAccess, EmptyState } from "@/components/common/States";
import { FormDialog, useAction } from "@/pages/ops/common";
import { DueBadge, StatusBadge, Pick, Pager, Table, money, label, Stat } from "./shared";
import { NewServiceDialog } from "./ServiceRecords";

const km = (v) => `${Number(v || 0).toLocaleString("en-IN")} km`;
const nextText = (ns) => (ns ? [ns.due_date && fmtDate(ns.due_date), ns.due_km && km(ns.due_km)].filter(Boolean).join(" or ") : "—");

export function DueList() {
  const { can } = useSession();
  const nav = useNavigate();
  const [due, setDue] = useState("due");
  const [page, setPage] = useState(1);
  const { data, loading, error, reload } = useFetch("/garage/vehicles", { due, page, limit: 25 }, can("vehicle.view") && can("maintenance.view"));
  if (!can("maintenance.view")) return <NoAccess perm="maintenance.view" />;
  const cols = [
    ["Registration", (r) => <span className="font-mono-g font-semibold">{r.registration_number}</span>],
    ["Model", (r) => `${r.make || ""} ${r.model || ""}`],
    ["Branch", (r) => r.branch_name || "—"],
    ["Odometer", (r) => km(r.odometer), "text-right whitespace-nowrap"],
    ["Last service", (r) => (r.last_service ? fmtDate(r.last_service) : "—")],
    ["Next service due", (r) => nextText(r.next_service)],
    ["Status", (r) => <DueBadge s={r.due_state} testId={`due-state-${r.id}`} />],
  ];
  return (
    <div data-testid="service-timeline-page">
      <PageHeader title="Service Timeline" subtitle="Services due within 7 days or 500 km, and overdue ones." />
      <div className="g39-card">
        <div className="p-4 border-b bg-secondary/40">
          <Pick value={due} onChange={(v) => { setDue(v); setPage(1); }} options={[{ value: "due", label: "Due soon and overdue" }, { value: "overdue", label: "Overdue only" }, { value: "upcoming", label: "Due soon only" }, { value: "scheduled", label: "Scheduled later" }, { value: "none", label: "Not scheduled" }]} testId="due-filter" className="sm:w-64" />
        </div>
        {loading && <LoadingRows rows={4} />}
        {error && <ErrorState message={error} onRetry={reload} />}
        {data && !data.items.length && <EmptyState title="Nothing here" body="No vehicles match this service status." testId="due-empty" />}
        {data && data.items.length > 0 && <Table cols={cols} rows={data.items} testId="due-table" onRow={(r) => nav(`/service-timeline/${r.id}`)} />}
        {data && <Pager page={page} total={data.total} limit={25} onPage={setPage} testId="due-pager" />}
      </div>
    </div>
  );
}

export function VehicleTimeline() {
  const { id } = useParams();
  const { can } = useSession();
  const { data, loading, error, reload } = useFetch(`/vehicles/${id}/service-timeline`);
  const { run } = useAction(reload);
  const [dlg, setDlg] = useState(null);
  if (loading && !data) return <div className="g39-card"><LoadingRows rows={6} /></div>;
  if (error) return <div className="g39-card"><ErrorState message={error} onRetry={reload} /></div>;
  const v = data.vehicle;
  const m = can("maintenance.manage");
  return (
    <div data-testid="vehicle-timeline-page">
      <Link to="/garage-vehicles" className="text-xs text-muted-foreground inline-flex items-center gap-1 mb-3 hover:text-gold" data-testid="back-to-vehicles"><ArrowLeft className="h-3 w-3" />Vehicles</Link>
      <PageHeader title={<span className="font-mono-g">{v.registration_number}</span>} subtitle={`${v.make} ${v.model} · ${v.branch_name || ""} · Driver: ${v.driver_name || "unassigned"}`}
        actions={m && <Button onClick={() => setDlg("new")} data-testid="timeline-new-service-btn"><Plus className="h-4 w-4 mr-1.5" />New service</Button>} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Odometer" value={km(v.odometer)} testId="timeline-odometer" />
        <Stat label="Services recorded" value={data.records.length} testId="timeline-count" />
        <Stat label="Maintenance spent" value={money(data.total_spent)} testId="timeline-spent" />
        <div className="g39-card p-4" data-testid="timeline-next">
          <div className="text-[12px] text-muted-foreground flex items-center justify-between">Next service <DueBadge s={v.due_state} testId="timeline-due-state" /></div>
          <div className="font-medium mt-1.5 text-sm" data-testid="timeline-next-value">{nextText(v.next_service)}</div>
          {m && <div className="flex gap-2 mt-2"><Button size="sm" variant="outline" onClick={() => setDlg("schedule")} data-testid="schedule-next-btn"><CalendarClock className="h-3.5 w-3.5 mr-1" />{v.next_service ? "Change" : "Schedule"}</Button>{v.next_service && <Button size="sm" variant="ghost" onClick={() => run("delete", `/vehicles/${id}/next-service`, null, "Schedule cleared")} data-testid="clear-next-btn">Clear</Button>}</div>}
        </div>
      </div>
      <div className="g39-card p-5" data-testid="timeline-list">
        <div className="font-display font-semibold mb-4">History (newest first)</div>
        {!data.records.length && <EmptyState title="No services recorded" body="Service records for this vehicle will appear here." testId="timeline-empty" />}
        <ol className="relative border-l border-border ml-2 space-y-5">
          {data.records.map((r) => (
            <li key={r.id} className="pl-5 relative" data-testid={`timeline-item-${r.id}`}>
              <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-gold" />
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold">{fmtDate(r.service_date)}</span>
                <Link to={`/service-records/${r.id}`} className="font-mono-g text-gold hover:underline" data-testid={`timeline-link-${r.id}`}>{r.number}</Link>
                <StatusBadge s={r.status} />
                <span className="text-muted-foreground capitalize">{label(r.service_type)} · {km(r.odometer)}</span>
              </div>
              <div className="text-sm mt-1">{r.complaint}</div>
              {r.work_performed && <div className="text-xs text-muted-foreground mt-0.5">Work: {r.work_performed}</div>}
              <div className="text-xs text-muted-foreground mt-0.5">{r.technician_or_garage || "—"} · Total {money(r.total_cost)}</div>
            </li>
          ))}
        </ol>
      </div>
      <NewServiceDialog open={dlg === "new"} onOpenChange={() => setDlg(null)} vehicleId={id} />
      <FormDialog open={dlg === "schedule"} onOpenChange={() => setDlg(null)} title="Schedule next service" description={`Set a date, an odometer reading, or both. Current odometer: ${km(v.odometer)}.`} testId="schedule-dialog"
        fields={[{ name: "due_date", label: "Due date", type: "date" }, { name: "due_km", label: "Due at odometer (km)", type: "number" }, { name: "notes", label: "Notes" }]}
        onSubmit={(x) => run("put", `/vehicles/${id}/next-service`, { due_date: x.due_date || null, due_km: x.due_km ? Number(x.due_km) : null, notes: x.notes || null }, "Next service scheduled")} />
    </div>
  );
}
